import React, { useState, useRef } from 'react';
import { Upload, Download, Copy, Play, Sparkles, FileText, Mic, Zap, Globe } from 'lucide-react';

const VoiceVerse = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedStyle, setSelectedStyle] = useState('');
  const [customPrompt, setCustomPrompt] = useState('');
  const [generatedText, setGeneratedText] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');

  const fileInputRef = useRef(null);

  // Professional Voice Style Database
  const voiceStyles = {
    documentary: {
      label: '🌍 Documentary',
      description: 'BBC Planet Earth style - Scientific & poetic',
      prompt: 'Write a BBC Planet Earth narrator-style voiceover for this scene using scientific and poetic tone, with deep atmosphere and quiet intensity. Focus on details and natural beauty.',
      example: '"In a world of digital realities, one image holds a story that reaches deeper than the surface..."'
    },
    trailer: {
      label: '🎬 Movie Trailer',
      description: 'Hollywood blockbuster - Dramatic & intense',
      prompt: 'Write a Hollywood movie trailer-style dramatic, intense narration with cliffhangers. Use short sentences, build tension with a grandiose tone. End with a call to action.',
      example: '"In a world where everything can change... WITH ONE WORD... This summer... prepare yourself..."'
    },
    motivation: {
      label: '💪 Motivational',
      description: 'Inspirational speaker - Energetic & uplifting',
      prompt: 'Write a motivational speech as if inspiring an audience to stand up and change their lives. Use powerful words, positive energy and direct call to action. Be energetic and uplifting.',
      example: '"This isn\'t just a moment - this is your chance to show the world who you really are!"'
    },
    mrbeast: {
      label: '😂 YouTuber Hype',
      description: 'MrBeast style - Hyped & engaging',
      prompt: 'Write text in the style of a hyperactive YouTuber like MrBeast! Excited, fast-paced, with superlatives and direct audience engagement. Use CAPITAL LETTERS for emphasis.',
      example: '"GUYS! You HAVE to see what just happened! This is the EASIEST way to..."'
    },
    freeman: {
      label: '🎭 Wise Narrator',
      description: 'Morgan Freeman style - Deep & philosophical',
      prompt: 'Write a calm, wise and reflective narration as if read by Morgan Freeman. Give deep meaning to the image and life message. Use metaphors and philosophical approach.',
      example: '"Sometimes, one image can say more than a thousand words. And sometimes, silence speaks the loudest..."'
    },
    educational: {
      label: '📚 Educational',
      description: 'Clear & informative teaching style',
      prompt: 'Write an educational narration that explains the topic in a clear and accessible way. Use examples and analogies. Be informative but not boring.',
      example: '"To understand this concept, we need to focus on three key elements..."'
    },
    podcast: {
      label: '🎙️ Podcast Intro',
      description: 'Conversational & engaging podcast style',
      prompt: 'Write a podcast-style introduction that\'s conversational and engaging. Make it feel like talking to a friend while maintaining professionalism.',
      example: '"Welcome back to the show! Today we\'re diving into something that caught my attention..."'
    },
    commercial: {
      label: '📺 Commercial',
      description: 'Professional advertisement tone',
      prompt: 'Write a professional commercial script that\'s persuasive yet trustworthy. Focus on benefits and include a clear call to action.',
      example: '"Discover the solution thousands are already using to transform their everyday experience..."'
    }
  };

  // File Upload Handler
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  // Simulate AI Text Generation with Enhanced Responses
  const generateVoiceover = async () => {
    if (!selectedFile && !customPrompt) {
      alert('Please upload an image or enter a description!');
      return;
    }

    if (!selectedStyle) {
      alert('Please select a narration style!');
      return;
    }

    setIsGenerating(true);
    
    // Simulate realistic AI processing time
    setTimeout(() => {
      const style = voiceStyles[selectedStyle];
      const baseContent = customPrompt || `Scene described from uploaded image`;
      
      // Enhanced AI-generated responses for each style
      const mockResponses = {
        documentary: `In the quiet depths of a digital moment, one frame reveals a story that stretches through time itself. Here, where technology meets the human spirit, we find testimony to our eternal quest for connection. Like a river that shapes stone over centuries, our choices sculpt the future we leave behind. This image captures more than pixels - it captures possibility.`,
        
        trailer: `In a world where everything seems perfect... A SECRET LIES HIDDEN. This image isn't what you think it is. In moments... everything will change. When reality bends... and truth becomes fiction... only one thing remains certain. PREPARE YOURSELF for a story that will leave you breathless. VoiceVerse... When words become reality. Coming this summer.`,
        
        motivation: `LOOK AT THIS IMAGE! Do you see the power hidden behind it? That's YOUR power! Every time you think you can't do something, remember this moment. You're not here by accident. You're here because you have a MISSION. Today is the day you stop dreaming and start LIVING! Your journey begins NOW! Don't let anyone tell you it's impossible - make it INEVITABLE!`,
        
        mrbeast: `GUYS! This is INSANE! I just uploaded this image and you WON'T BELIEVE what happened! This VoiceVerse tool is CRAZY good! In just 30 seconds I get a PERFECT voiceover script! Make sure to SMASH that like button if you want to see more AI tools like this! This is going to change EVERYTHING for content creators!`,
        
        freeman: `Sometimes we pause before an image and ask ourselves... what is it telling us? It doesn't speak about what we see, but about who we are when we look. In this moment, in this frame, lies a truth older than time itself - that beauty is in the eye of the beholder, and wisdom lives in the heart of those who listen.`,
        
        educational: `Let's analyze this image step by step. First, we'll notice the composition - how elements are arranged to guide our attention. Second, the lighting and how it affects the overall mood. Third, the color palette and its psychological impact. By understanding these fundamental principles, we can decode the message the creator intended to convey.`,
        
        podcast: `Welcome back to the show, everyone! So I came across this image today, and it got me thinking about something we don't talk about enough. You know that feeling when you see something that just... clicks? That's exactly what happened here. Let me walk you through what I discovered, because I think it's going to change how you see things too.`,
        
        commercial: `Tired of spending hours writing the perfect script? Introducing VoiceVerse - the revolutionary AI tool that transforms any image into professional-quality narration in seconds. Join thousands of content creators who are already saving time and creating better content. Try VoiceVerse today and experience the future of content creation. Your audience deserves the best - give it to them.`
      };

      setGeneratedText(mockResponses[selectedStyle] || 'Generated professional voiceover text ready for use...');
      setIsGenerating(false);
    }, 2500);
  };

  // Copy to Clipboard with feedback
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedText);
      alert('✅ Text copied to clipboard!');
    } catch (err) {
      alert('❌ Failed to copy text');
    }
  };

  // Download as TXT
  const downloadTxt = () => {
    const element = document.createElement('a');
    const file = new Blob([generatedText], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `voiceover_${selectedStyle}_${Date.now()}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const quickPrompts = [
    "A person working late at night with city lights in the background",
    "Sunrise over mountains with fog rolling through valleys",
    "Modern technology device on a minimalist desk setup",
    "Group of friends laughing together at a coffee shop"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white">
      {/* Header */}
      <div className="text-center py-12">
        <div className="flex items-center justify-center gap-3 mb-4">
          <Mic className="text-purple-400 animate-pulse" size={48} />
          <h1 className="text-6xl font-bold bg-gradient-to-r from-purple-400 via-blue-400 to-cyan-400 bg-clip-text text-transparent">
            VoiceVerse
          </h1>
          <Sparkles className="text-cyan-400 animate-bounce" size={48} />
        </div>
        <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
          Transform any image into professional voiceover scripts instantly. 
          Choose your style, upload your content, get perfect narration for TikTok, YouTube, and beyond!
        </p>
        <div className="flex items-center justify-center gap-2 mt-4 text-sm text-purple-300">
          <Globe size={16} />
          <span>Built for MGX AI Tools Challenge 2025</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 pb-12">
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
          
          {/* Left Panel - Input */}
          <div className="space-y-6">
            
            {/* File Upload */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 hover:bg-white/15 transition-all duration-300">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Upload size={24} className="text-blue-400" />
                Upload Image or Describe Scene
              </h3>
              
              <div className="space-y-4">
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-white/30 rounded-xl p-8 text-center cursor-pointer hover:border-purple-400 hover:bg-white/5 transition-all duration-300 group"
                >
                  {previewUrl ? (
                    <div className="relative">
                      <img src={previewUrl} alt="Preview" className="max-h-48 mx-auto rounded-lg shadow-lg" />
                      <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                        <span className="text-white font-medium">Click to change image</span>
                      </div>
                    </div>
                  ) : (
                    <div className="group-hover:scale-105 transition-transform">
                      <Upload size={48} className="mx-auto mb-4 text-gray-400 group-hover:text-purple-400 transition-colors" />
                      <p className="text-lg">Click to upload image</p>
                      <p className="text-sm text-gray-400 mt-2">Supports JPG, PNG, WebP</p>
                    </div>
                  )}
                </div>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                
                <div className="text-center text-gray-400 font-medium">OR</div>
                
                <textarea
                  value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  placeholder="Describe the scene or topic you want a voiceover for..."
                  className="w-full h-32 px-4 py-3 bg-white/10 border border-white/20 rounded-xl focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-400/20 resize-none transition-all"
                />
                
                {/* Quick Prompts */}
                <div>
                  <p className="text-sm text-gray-400 mb-2">Quick prompts:</p>
                  <div className="flex flex-wrap gap-2">
                    {quickPrompts.map((prompt, index) => (
                      <button
                        key={index}
                        onClick={() => setCustomPrompt(prompt)}
                        className="px-3 py-1 text-xs bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg transition-colors"
                      >
                        {prompt.split(' ').slice(0, 4).join(' ')}...
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Style Selection */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Zap size={24} className="text-yellow-400" />
                Choose Narration Style
              </h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {Object.entries(voiceStyles).map(([key, style]) => (
                  <button
                    key={key}
                    onClick={() => setSelectedStyle(key)}
                    className={`p-4 rounded-xl border-2 text-left transition-all duration-300 hover:scale-105 ${
                      selectedStyle === key
                        ? 'border-purple-400 bg-purple-400/20 shadow-lg shadow-purple-400/20'
                        : 'border-white/20 bg-white/5 hover:border-white/40 hover:bg-white/10'
                    }`}
                  >
                    <div className="font-bold text-lg">{style.label}</div>
                    <div className="text-sm text-gray-300 mt-1">{style.description}</div>
                    <div className="text-xs text-gray-400 mt-2 italic leading-relaxed">{style.example}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Generate Button */}
            <button
              onClick={generateVoiceover}
              disabled={isGenerating}
              className="w-full py-4 bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl font-bold text-lg hover:from-purple-600 hover:to-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all duration-300 hover:scale-105 shadow-lg shadow-purple-500/25"
            >
              {isGenerating ? (
                <>
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                  Generating Magic...
                </>
              ) : (
                <>
                  <Sparkles size={24} />
                  Generate VoiceVerse
                </>
              )}
            </button>
          </div>

          {/* Right Panel - Output */}
          <div className="space-y-6">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <FileText size={24} className="text-green-400" />
                Generated Voiceover Script
              </h3>
              
              {generatedText ? (
                <div className="space-y-4">
                  <div className="bg-black/30 border border-white/10 rounded-xl p-6 min-h-[300px] relative overflow-hidden">
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 to-blue-500"></div>
                    <p className="leading-relaxed text-lg">{generatedText}</p>
                    <div className="absolute bottom-2 right-2 text-xs text-gray-500">
                      {generatedText.split(' ').length} words • ~{Math.ceil(generatedText.split(' ').length / 150)} min read
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={copyToClipboard}
                      className="py-3 bg-green-600 hover:bg-green-700 rounded-xl flex items-center justify-center gap-2 transition-all duration-300 hover:scale-105 shadow-lg"
                    >
                      <Copy size={20} />
                      Copy Script
                    </button>
                    
                    <button
                      onClick={downloadTxt}
                      className="py-3 bg-blue-600 hover:bg-blue-700 rounded-xl flex items-center justify-center gap-2 transition-all duration-300 hover:scale-105 shadow-lg"
                    >
                      <Download size={20} />
                      Download .txt
                    </button>
                  </div>

                  <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-xl p-4">
                    <h4 className="font-bold mb-2 flex items-center gap-2">
                      💡 Pro Tips for Content Creators:
                    </h4>
                    <ul className="text-sm space-y-1 text-gray-200">
                      <li>• Copy script directly into CapCut for instant video production</li>
                      <li>• Use with Suno.ai or ElevenLabs for AI voice generation</li>
                      <li>• Perfect length for TikTok/YouTube Shorts (15-60 seconds)</li>
                      <li>• Adjust pacing and add pauses for better delivery</li>
                    </ul>
                  </div>
                </div>
              ) : (
                <div className="bg-black/30 border border-white/10 rounded-xl p-8 min-h-[300px] flex items-center justify-center">
                  <div className="text-center">
                    <Mic size={64} className="mx-auto mb-4 opacity-30 animate-pulse" />
                    <p className="text-gray-400 text-lg mb-2">Your professional voiceover script will appear here</p>
                    <p className="text-gray-500 text-sm">Ready to transform any image into compelling narration</p>
                  </div>
                </div>
              )}
            </div>

            {/* Feature Highlights */}
            <div className="bg-gradient-to-r from-purple-500/20 to-blue-500/20 border border-purple-400/30 rounded-2xl p-6">
              <h4 className="font-bold mb-4 flex items-center gap-2">
                <Sparkles size={20} className="text-purple-400" />
                What Makes VoiceVerse Special:
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2">
                  <Zap size={16} className="text-yellow-400" />
                  <span>8 Professional Styles</span>
                </div>
                <div className="flex items-center gap-2">
                  <Globe size={16} className="text-blue-400" />
                  <span>Universal Content Format</span>
                </div>
                <div className="flex items-center gap-2">
                  <FileText size={16} className="text-green-400" />
                  <span>Instant Export Options</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mic size={16} className="text-purple-400" />
                  <span>Ready for Voice AI</span>
                </div>
              </div>
            </div>

            {/* Coming Soon */}
            <div className="bg-gradient-to-r from-cyan-500/20 to-purple-500/20 border border-cyan-400/30 rounded-2xl p-6">
              <h4 className="font-bold mb-3 flex items-center gap-2">
                🚀 Coming Soon - Pro Features:
              </h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <Play size={16} className="text-cyan-400" />
                  Direct AI voice generation with realistic voices
                </li>
                <li className="flex items-center gap-2">
                  <Sparkles size={16} className="text-purple-400" />
                  Custom brand voice training and style creation
                </li>
                <li className="flex items-center gap-2">
                  <Zap size={16} className="text-yellow-400" />
                  Batch processing for multiple images at once
                </li>
                <li className="flex items-center gap-2">
                  <Globe size={16} className="text-green-400" />
                  Multi-language support (20+ languages)
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="text-center py-8 border-t border-white/10 bg-black/20">
        <div className="max-w-4xl mx-auto px-4">
          <p className="text-gray-400 mb-2">
            Made with 💜 for content creators worldwide
          </p>
          <p className="text-sm text-gray-500">
            VoiceVerse v1.0 | MGX AI Tools Challenge 2025 | Transform Ideas Into Voices
          </p>
        </div>
      </footer>
    </div>
  );
};

export default VoiceVerse;
